import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Route as DashboardRoute } from "@/routes/_app/_auth/dashboard/_layout.index";
import type { FieldApi } from "@tanstack/react-form";
import { Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { api } from "~/convex/_generated/api";
import { useAuthActions } from "@convex-dev/auth/react";
import { useForm } from "@tanstack/react-form";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function FieldInfo({ field }: { field: FieldApi<any, any> }) {
  return (
    <>
      {field.state.meta.isTouched && field.state.meta.errors.length > 0 ? (
        <span className="text-sm text-destructive">
          {field.state.meta.errors.join(", ")}
        </span>
      ) : null}
    </>
  );
}

export const Route = createFileRoute(
  "/_app/_auth/onboarding/_layout/creator_onboarding",
)({
  component: CreatorOnboarding,
  beforeLoad: () => ({
    title: `Creator Profile`,
  }),
});

export default function CreatorOnboarding() {
  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));
  const { data: creator } = useQuery(
    convexQuery(api.creators.getCreatorByUserId, {
      userId: user?._id,
    }),
  );
  const { mutateAsync: createCreator } = useMutation({
    mutationFn: useConvexMutation(api.creators.updateCreator),
  });
  const { signIn } = useAuthActions();
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: {
      bio: "",
    },
    onSubmit: async ({ value }) => {
      await createCreator(value);
      navigate({ to: DashboardRoute.fullPath });
    },
  });

  return (
    <div className="mx-auto flex h-full w-full max-w-xl flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <h3 className="text-center text-2xl font-medium text-primary">
          Complete Your Creator Profile
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          Tell us about your content creation journey
        </p>
      </div>

      <form
        className="flex w-full flex-col items-start gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="flex w-full flex-col gap-4">
          <form.Subscribe
            selector={(state) => ({
              isTouched: state.isTouched,
            })}
          >
            {(state) => {
              console.log(state);
              return null;
            }}
          </form.Subscribe>

          <p className="text-sm text-primary/60">
            Connect at least one social media account. Don't worry, you can
            always add more accounts later in your settings!
          </p>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="tiktokHandle"
              className="text-sm font-medium text-primary"
            >
              TikTok Account
            </label>
            {creator?.tiktokHandles?.length ? (
              <p className="text-sm text-primary/60">
                @{creator.tiktokHandles[0].handle}
              </p>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full bg-transparent"
                onClick={async () => {
                  await signIn("tiktok", {
                    redirectTo: Route.fullPath,
                  });
                }}
              >
                <svg
                  className="mr-2 h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0011.14-4.02v-6.3a8.16 8.16 0 004.65 1.49v-3.39a4.85 4.85 0 01-1.2-.19z" />
                </svg>
                Connect TikTok
              </Button>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="instagramHandle"
              className="text-sm font-medium text-primary"
            >
              Instagram Handle
            </label>
            {creator?.instagramHandles?.length ? (
              <p className="text-sm text-primary/60">
                @{creator.instagramHandles[0].handle}
              </p>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full bg-transparent"
                onClick={async () => {
                  await signIn("instagram", {
                    redirectTo: Route.fullPath,
                  });
                }}
              >
                <svg
                  className="mr-2 h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
                Connect Instagram
              </Button>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="youtubeHandle"
              className="text-sm font-medium text-primary"
            >
              YouTube Channel
            </label>
            {creator?.youtubeHandles?.length ? (
              <p className="text-sm text-primary/60">
                {creator.youtubeHandles[0].handle}
              </p>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full bg-transparent"
                onClick={async () => {
                  await signIn("youtube", {
                    redirectTo: Route.fullPath,
                  });
                }}
              >
                <svg
                  className="mr-2 h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
                Connect YouTube
              </Button>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="bio" className="text-sm font-medium text-primary">
              Bio
            </label>
            <form.Field
              name="bio"
              validators={{
                onChange: ({ value }) => {
                  if (!value?.trim()) {
                    return "Please tell us a bit about yourself";
                  }
                  if (value.length > 500) {
                    return "Bio is too long";
                  }
                  return undefined;
                },
              }}
              children={(field) => (
                <>
                  <Textarea
                    placeholder="Tell us about your content and what you're passionate about..."
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    className={`min-h-[100px] bg-transparent ${
                      field.state.meta.errors.length > 0 &&
                      "border-destructive focus-visible:ring-destructive"
                    }`}
                  />
                  <FieldInfo field={field} />
                </>
              )}
            />
          </div>
        </div>

        <form.Subscribe
          selector={(state) => ({
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
          })}
        >
          {(state) => {
            const hasSocialMedia = Boolean(
              creator?.instagramHandles?.length ||
                creator?.tiktokHandles?.length ||
                creator?.youtubeHandles?.length,
            );

            return (
              <Button
                type="submit"
                size="sm"
                className="w-full"
                disabled={
                  !state.canSubmit || state.isSubmitting || !hasSocialMedia
                }
              >
                {state.isSubmitting ? (
                  <Loader2 className="animate-spin" />
                ) : hasSocialMedia ? (
                  "Complete Profile"
                ) : (
                  "Connect at least one social media account"
                )}
              </Button>
            );
          }}
        </form.Subscribe>
      </form>

      <p className="px-6 text-center text-sm font-normal leading-normal text-primary/60">
        You can update your profile information at any time from your account
        settings.
      </p>
    </div>
  );
}
